import { useEffect, useMemo, useRef, useState } from 'react';
import { ToastQueue } from '@adobe/react-spectrum';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  selectAudiences,
  selectCategories,
  selectEvents,
  selectFeatures,
  selectFilteredReleases,
  selectGenAiFilter,
  selectLanesTiers,
  selectLoading,
  selectMomentTypeFilter,
  selectProductFilter,
  selectProductGroups,
  selectReleases,
  selectSearchFilterActive
} from 'app/pages/Home/slice/selectors';

/**
 * Mirrors Roadmap.tsx no-results toast behavior for the monthly timeline view.
 */
export function useMonthlyNoDataToast(): void {
  const location = useLocation();
  const isSlideoutOpen = location.pathname.includes('/moments/');
  const noDataAlertTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const loading = useSelector(selectLoading);
  const releases = useSelector(selectReleases);
  const filteredReleases = useSelector(selectFilteredReleases);
  const searchFilterActive = useSelector(selectSearchFilterActive);
  const categories = useSelector(selectCategories);
  const productGroups = useSelector(selectProductGroups);
  const audiences = useSelector(selectAudiences);
  const selectedEvents = useSelector(selectEvents);
  const selectedFeatures = useSelector(selectFeatures);
  const selectedProducts = useSelector(selectProductFilter);
  const genAiFilter = useSelector(selectGenAiFilter);
  const lanesTiers = useSelector(selectLanesTiers);
  const momentTypeFilter = useSelector(selectMomentTypeFilter);

  useEffect(() => {
    if (
      !initialLoadComplete &&
      !loading &&
      releases?.releaseScheduled &&
      releases.releaseScheduled.length > 0
    ) {
      setInitialLoadComplete(true);
    }
  }, [initialLoadComplete, loading, releases?.releaseScheduled]);

  const hasNoData = useMemo(() => {
    if (loading) return false;

    if (!initialLoadComplete) {
      return false;
    }

    if (!releases?.releaseScheduled || releases.releaseScheduled.length === 0) {
      return false;
    }

    if (searchFilterActive) {
      return false;
    }

    const hasActiveFilters =
      categories.selectedCats.length > 0 ||
      productGroups.selectedGroups.length > 0 ||
      audiences.selectedAudiences.length > 0 ||
      selectedEvents.selectedEvents.length > 0 ||
      selectedFeatures.selectedFeatures.length > 0 ||
      selectedProducts.selectedProducts.length > 0 ||
      genAiFilter.selectedFilters.length > 0 ||
      lanesTiers.selectedLanesTiers.length > 0 ||
      momentTypeFilter.selectedMomentTypes.length > 0;

    if (!hasActiveFilters) {
      return false;
    }

    const mainReleases = filteredReleases?.filter(
      release =>
        release.type !== 'Key Dates' && !release.type.includes('Events')
    );

    const hasMainReleases =
      mainReleases &&
      mainReleases.length > 0 &&
      mainReleases.some(
        release =>
          release.projects &&
          release.projects.length > 0 &&
          release.projects.some(
            project => project.project && project.project.length > 0
          )
      );

    return !hasMainReleases;
  }, [
    loading,
    filteredReleases,
    releases,
    categories.selectedCats,
    productGroups.selectedGroups,
    audiences.selectedAudiences,
    selectedEvents.selectedEvents,
    selectedFeatures.selectedFeatures,
    selectedProducts.selectedProducts,
    genAiFilter.selectedFilters,
    lanesTiers.selectedLanesTiers,
    momentTypeFilter.selectedMomentTypes,
    searchFilterActive,
    initialLoadComplete
  ]);

  useEffect(() => {
    return () => {
      if (noDataAlertTimerRef.current) {
        clearTimeout(noDataAlertTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (noDataAlertTimerRef.current) {
      clearTimeout(noDataAlertTimerRef.current);
    }

    if (hasNoData && !isSlideoutOpen) {
      noDataAlertTimerRef.current = setTimeout(() => {
        ToastQueue.info(
          'No results found for the selected filters. Try adjusting your filter criteria.',
          { id: 'no-data-alert', timeout: 5000 }
        );
      }, 300);
    }

    return () => {
      if (noDataAlertTimerRef.current) {
        clearTimeout(noDataAlertTimerRef.current);
      }
    };
  }, [hasNoData, isSlideoutOpen]);
}
